import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronRight, Star, Heart, Zap, Sparkles, Trophy } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import STORY_GAMES from './StoryGameData';
import confetti from 'canvas-confetti';

// Typewriter hook
const useTypewriter = (text, speed = 30) => {
  const [displayed, setDisplayed] = useState('');
  const [done, setDone] = useState(false);
  useEffect(() => {
    setDisplayed(''); setDone(false);
    let i = 0;
    const timer = setInterval(() => {
      if (i < text.length) { setDisplayed(text.slice(0, i + 1)); i++; }
      else { setDone(true); clearInterval(timer); }
    }, speed);
    return () => clearInterval(timer);
  }, [text, speed]);
  return { displayed, done, skip: () => { setDisplayed(text); setDone(true); } };
};

// Floating score animation
const FloatingScore = ({ score, show }) => (
  <AnimatePresence>
    {show && (
      <motion.div initial={{ opacity: 1, y: 0, scale: 1 }} animate={{ opacity: 0, y: -80, scale: 1.5 }} exit={{ opacity: 0 }}
        transition={{ duration: 1.2 }} className="absolute top-1/2 left-1/2 -translate-x-1/2 pointer-events-none z-50">
        <span className="text-4xl font-black text-[#FFC03D] drop-shadow-lg">+{score}</span>
      </motion.div>
    )}
  </AnimatePresence>
);

const StoryGameEngine = () => {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const tutorName = user?.tutor_name || 'Buddy';
  const game = STORY_GAMES[gameId];

  const [sceneIndex, setSceneIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(3);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [totalCorrect, setTotalCorrect] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [showFloatingScore, setShowFloatingScore] = useState(false);
  const [sceneKey, setSceneKey] = useState(0);
  const [shakeWrong, setShakeWrong] = useState(false);

  const scene = game ? game.scenes[sceneIndex] : null;

  // Fire confetti on ending
  useEffect(() => {
    if (scene && scene.type === 'ending') {
      confetti({ particleCount: 150, spread: 90, origin: { y: 0.6 } });
    }
  }, [scene]);

  if (!game) return (
    <div className="min-h-screen bg-[#F4F8FB] flex items-center justify-center">
      <p className="text-xl">Game not found</p>
      <button onClick={() => navigate('/games')} className="ml-4 px-6 py-3 bg-[#4D96FF] text-white rounded-2xl font-bold">Back</button>
    </div>
  );

  const progress = ((sceneIndex + 1) / game.scenes.length) * 100;

  const handleAnswer = (answer) => {
    if (showResult) return;
    setSelectedAnswer(answer);
    setShowResult(true);
    setTotalQuestions(t => t + 1);

    if (answer === scene.correct) {
      setScore(s => s + 20);
      setTotalCorrect(t => t + 1);
      setShowFloatingScore(true);
      setTimeout(() => setShowFloatingScore(false), 1200);
    } else {
      setLives(l => Math.max(0, l - 1));
      setShakeWrong(true);
      setTimeout(() => setShakeWrong(false), 600);
    }
  };

  const nextScene = () => {
    setSelectedAnswer(null);
    setShowResult(false);
    setShowHint(false);
    setSceneKey(k => k + 1);
    if (sceneIndex < game.scenes.length - 1) setSceneIndex(s => s + 1);
  };

  // ========= ENDING =========
  if (scene.type === 'ending') {
    const stars = totalQuestions > 0 ? Math.ceil((totalCorrect / totalQuestions) * 3) : 3;

    return (
      <div className={`min-h-screen bg-gradient-to-br ${game.color} flex items-center justify-center p-4`} data-testid="story-game-ending">
        <motion.div initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: 'spring', duration: 0.8 }}
          className="bg-white/95 backdrop-blur-xl rounded-3xl p-10 max-w-lg w-full text-center shadow-2xl">
          <motion.div initial={{ rotateY: 180 }} animate={{ rotateY: 0 }} transition={{ delay: 0.3, duration: 0.6 }} className="text-7xl mb-4">🏆</motion.div>
          <motion.h1 initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.5 }}
            className="text-4xl font-black text-[#1E293B] mb-4">Quest Complete!</motion.h1>
          <motion.p initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.7 }}
            className="text-lg text-[#64748B] mb-6">{scene.text}</motion.p>
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 1, type: 'spring' }} className="flex justify-center gap-3 mb-6">
            {[1, 2, 3].map(i => (
              <motion.div key={i} initial={{ rotateZ: -180, opacity: 0 }} animate={{ rotateZ: 0, opacity: 1 }} transition={{ delay: 1 + i * 0.2 }}>
                <Star className={`w-14 h-14 ${i <= stars ? 'text-[#FFC03D] fill-[#FFC03D]' : 'text-slate-200'} drop-shadow-lg`} />
              </motion.div>
            ))}
          </motion.div>
          <div className="grid grid-cols-3 gap-4 mb-8">
            <motion.div initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 1.5 }} className="bg-[#D8E8FF] rounded-2xl p-4">
              <p className="text-3xl font-black text-[#4D96FF]">{score}</p><p className="text-xs font-bold text-[#64748B]">Points</p>
            </motion.div>
            <motion.div initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 1.7 }} className="bg-[#E8F5E9] rounded-2xl p-4">
              <p className="text-3xl font-black text-[#38E54D]">{totalCorrect}/{totalQuestions}</p><p className="text-xs font-bold text-[#64748B]">Correct</p>
            </motion.div>
            <motion.div initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 1.9 }} className="bg-[#FFF3D6] rounded-2xl p-4">
              <p className="text-3xl font-black text-[#FFC03D]">{lives}</p><p className="text-xs font-bold text-[#64748B]">Lives</p>
            </motion.div>
          </div>
          <div className="space-y-3">
            <button onClick={() => { setSceneIndex(0); setScore(0); setLives(3); setTotalCorrect(0); setTotalQuestions(0); setSceneKey(0); }}
              className={`w-full py-4 bg-gradient-to-r ${game.color} text-white rounded-2xl font-bold text-lg border-b-4 border-black/20 active:border-b-0 active:translate-y-1 transition-all`}>Play Again</button>
            <button onClick={() => navigate('/games/story')} className="w-full py-4 bg-slate-100 text-[#1E293B] rounded-2xl font-bold">More Story Games</button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-gradient-to-br ${game.color} relative overflow-hidden`} data-testid="story-game-engine">
      {/* Animated background particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(6)].map((_, i) => (
          <motion.div key={i} className="absolute w-4 h-4 bg-white/10 rounded-full"
            initial={{ x: Math.random() * 1920, y: -20 }}
            animate={{ y: 900, x: Math.random() * 1920, rotate: 360 }}
            transition={{ duration: 8 + Math.random() * 4, repeat: Infinity, delay: i * 1.5 }} />
        ))}
      </div>

      {/* Header */}
      <motion.div initial={{ y: -50 }} animate={{ y: 0 }} className="bg-black/30 backdrop-blur-md relative z-10">
        <div className="max-w-4xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <button onClick={() => navigate('/games/story')} className="text-white/80 hover:text-white"><ArrowLeft className="w-6 h-6" /></button>
            <div className="flex items-center gap-4">
              {/* Animated hearts */}
              <div className="flex gap-1">
                {[1,2,3].map(i => (
                  <motion.div key={i} animate={lives < i ? { scale: [1, 0], opacity: [1, 0] } : { scale: 1 }} transition={{ duration: 0.3 }}>
                    <Heart className={`w-7 h-7 ${i <= lives ? 'text-red-400 fill-red-400 drop-shadow-lg' : 'text-white/20'}`} />
                  </motion.div>
                ))}
              </div>
              <motion.div animate={{ scale: showFloatingScore ? [1, 1.3, 1] : 1 }} className="bg-white/20 px-5 py-1.5 rounded-xl text-white font-black flex items-center gap-1 text-lg">
                <Zap className="w-5 h-5" />{score}
              </motion.div>
            </div>
          </div>
          {/* Animated progress */}
          <div className="mt-3 bg-white/20 rounded-full h-2.5 overflow-hidden">
            <motion.div className="bg-white rounded-full h-2.5" initial={{ width: 0 }} animate={{ width: `${progress}%` }} transition={{ duration: 0.5 }} />
          </div>
        </div>
      </motion.div>

      {/* Floating Score */}
      <FloatingScore score={20} show={showFloatingScore} />

      {/* Scene Content */}
      <div className="max-w-4xl mx-auto px-4 py-8 relative z-10">
        <AnimatePresence mode="wait">
          <motion.div key={sceneKey} initial={{ opacity: 0, x: 100 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -100 }}
            transition={{ type: 'spring', duration: 0.5 }}
            className="bg-white/95 backdrop-blur-xl rounded-3xl p-8 shadow-2xl min-h-[380px] flex flex-col justify-between relative overflow-hidden">
            
            {/* Decorative corner glow */}
            <div className="absolute -top-10 -right-10 w-40 h-40 bg-gradient-to-br from-[#FFC03D]/20 to-transparent rounded-full blur-2xl"></div>

            {/* INTRO / STORY */}
            {(scene.type === 'intro' || scene.type === 'story') && (
              <StoryScene scene={scene} tutorName={tutorName} onContinue={nextScene} gameColor={game.color} />
            )}

            {/* CHALLENGE */}
            {scene.type === 'challenge' && (
              <ChallengeScene scene={scene} tutorName={tutorName} selectedAnswer={selectedAnswer} showResult={showResult}
                showHint={showHint} shakeWrong={shakeWrong} onAnswer={handleAnswer} onNext={nextScene} onHint={() => setShowHint(true)} />
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

// Story/Intro Scene with typewriter
const StoryScene = ({ scene, tutorName, onContinue, gameColor }) => {
  const { displayed, done, skip } = useTypewriter(scene.text, 25);

  return (
    <div className="flex-1 flex flex-col items-center justify-center text-center py-4" onClick={!done ? skip : undefined}>
      {/* Character avatar */}
      <motion.div initial={{ scale: 0, rotate: -180 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: 'spring', duration: 0.6 }}
        className="w-20 h-20 bg-gradient-to-br from-[#FFC03D] to-orange-500 rounded-3xl flex items-center justify-center mb-4 shadow-xl">
        <motion.div animate={{ y: [0, -5, 0] }} transition={{ repeat: Infinity, duration: 2 }}>
          <Sparkles className="w-10 h-10 text-white" />
        </motion.div>
      </motion.div>

      {/* Character name */}
      {scene.character && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
          className="mb-4 inline-block px-5 py-1.5 bg-gradient-to-r from-[#FFC03D]/20 to-orange-100 text-[#E6A820] rounded-full text-sm font-black border border-[#FFC03D]/30">
          {scene.character === 'AI Tutor' ? tutorName : scene.character}
        </motion.div>
      )}

      {/* Typewriter text */}
      <p className="text-2xl font-bold text-[#1E293B] leading-relaxed max-w-2xl min-h-[80px]">
        {displayed}
        {!done && <motion.span animate={{ opacity: [1, 0] }} transition={{ repeat: Infinity, duration: 0.5 }} className="inline-block w-0.5 h-6 bg-[#4D96FF] ml-1"></motion.span>}
      </p>

      {/* Continue button - appears after typewriter finishes */}
      <AnimatePresence>
        {done && (
          <motion.button initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ type: 'spring' }}
            onClick={onContinue} data-testid="continue-btn"
            className={`mt-8 px-10 py-4 bg-gradient-to-r ${gameColor} text-white rounded-2xl font-bold text-lg hover:scale-105 active:scale-95 transition-all flex items-center gap-2 shadow-xl border-b-4 border-black/20`}>
            Continue <ChevronRight className="w-5 h-5" />
          </motion.button>
        )}
      </AnimatePresence>
      {!done && <p className="mt-6 text-sm text-[#64748B]">(tap to skip)</p>}
    </div>
  );
};

// Challenge Scene with animations
const ChallengeScene = ({ scene, tutorName, selectedAnswer, showResult, showHint, shakeWrong, onAnswer, onNext, onHint }) => (
  <div>
    <motion.p initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="text-lg text-[#64748B] font-semibold mb-2">{scene.text}</motion.p>
    <motion.h2 initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
      className="text-2xl font-black text-[#1E293B] mb-8">{scene.question}</motion.h2>

    <motion.div className={`grid grid-cols-1 md:grid-cols-2 gap-4 ${shakeWrong ? '' : ''}`}>
      {scene.options.map((opt, i) => {
        const isCorrect = showResult && opt === scene.correct;
        const isWrong = showResult && opt === selectedAnswer && opt !== scene.correct;
        return (
          <motion.button key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 + i * 0.1 }}
            whileHover={!showResult ? { scale: 1.03 } : {}} whileTap={!showResult ? { scale: 0.97 } : {}}
            onClick={() => onAnswer(opt)} disabled={showResult} data-testid={`story-option-${i}`}
            className={`p-5 rounded-2xl border-3 text-left font-semibold text-lg transition-all relative overflow-hidden ${
              isCorrect ? 'bg-green-100 border-green-500 text-green-800' :
              isWrong ? 'bg-red-100 border-red-500 text-red-800 animate-[shake_0.5s_ease-in-out]' :
              showResult ? 'bg-slate-50 border-slate-200 text-slate-400' :
              'bg-[#F4F8FB] border-slate-200 hover:border-[#4D96FF] hover:bg-blue-50 text-[#1E293B]'
            }`}>
            {isCorrect && <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="absolute top-2 right-2"><Sparkles className="w-5 h-5 text-green-500" /></motion.div>}
            <span className="text-[#64748B] font-bold mr-2">{String.fromCharCode(65+i)}.</span>{opt}
          </motion.button>
        );
      })}
    </motion.div>

    {/* Result */}
    <AnimatePresence>
      {showResult && (
        <motion.div initial={{ opacity: 0, y: 20, height: 0 }} animate={{ opacity: 1, y: 0, height: 'auto' }} transition={{ type: 'spring' }}
          className={`mt-6 p-5 rounded-2xl ${selectedAnswer === scene.correct ? 'bg-green-50 border-2 border-green-300' : 'bg-orange-50 border-2 border-orange-300'}`}>
          <motion.p initial={{ x: -20 }} animate={{ x: 0 }} className="font-bold text-lg mb-1">
            {selectedAnswer === scene.correct ? '✨ Brilliant! You got it!' : `Almost! The answer is: ${scene.correct}`}
          </motion.p>
          <p className="text-[#64748B]">{tutorName} says: {scene.hint}</p>
          <motion.button initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
            onClick={onNext} data-testid="next-scene-btn"
            className="mt-4 px-8 py-3 bg-gradient-to-r from-[#4D96FF] to-[#A493FF] text-white rounded-2xl font-bold hover:scale-105 active:scale-95 transition-all flex items-center gap-2 shadow-lg">
            Continue Story <ChevronRight className="w-5 h-5" />
          </motion.button>
        </motion.div>
      )}
    </AnimatePresence>

    {/* Hint */}
    {!showResult && !showHint && (
      <motion.button initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5 }}
        onClick={onHint} className="mt-4 text-sm font-bold text-[#FFC03D] hover:underline" data-testid="hint-btn">
        💡 {tutorName}, give me a hint!
      </motion.button>
    )}
    <AnimatePresence>
      {showHint && !showResult && (
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
          className="mt-4 p-4 bg-[#FFF3D6] rounded-2xl border-2 border-[#FFC03D]">
          <p className="font-semibold text-[#E6A820]">💡 {tutorName}: {scene.hint}</p>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

export default StoryGameEngine;
